import { useEffect, useMemo, useState } from "react";
import { X, Send, Loader2, ExternalLink, CheckCircle2, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * CreativeStudioBlogPublishModal — publish a Creative Studio image
 * straight to the connected WordPress site as a blog post's featured
 * image.
 *
 * Flow:
 *   1. On open, check /blog-connector/wordpress/connection to make sure
 *      WordPress is connected. If not, deep-link to /dashboard/blog.
 *   2. Prefill title/excerpt/content from the image's title + prompt.
 *   3. Base64-encode the image (fetched via authed API) and POST to
 *      /blog-connector/wordpress/publish with featured_image_b64 +
 *      content_html.
 *
 * Props:
 *   open        bool
 *   image       {image_id, title?, prompt?, tags?}
 *   onClose()   —
 */
export default function CreativeStudioBlogPublishModal({ open, image, onClose }) {
  const [connState, setConnState] = useState(null);  // null=loading, {connected, site_url, ...}
  const [imgB64, setImgB64] = useState(null);
  const [imgLoading, setImgLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [content, setContent] = useState("");
  const [status, setStatus] = useState("draft");     // draft | publish
  const [publishing, setPublishing] = useState(false);
  const [result, setResult] = useState(null);        // {post_url, post_id}

  const defaultTitle = useMemo(() => {
    if (!image) return "";
    return image.title || (image.prompt || "AI image").slice(0, 70);
  }, [image]);

  // Reset state each time we open on a fresh image
  useEffect(() => {
    if (!open || !image) return;
    setTitle(defaultTitle);
    setExcerpt((image.prompt || "").slice(0, 200));
    const promptBlock = image.prompt
      ? `<p><em>Generated with BroadcastFlow AI · Creative Studio</em></p><blockquote>${image.prompt}</blockquote>`
      : "<p><em>Generated with BroadcastFlow AI · Creative Studio</em></p>";
    setContent(`<p>${defaultTitle}</p>\n${promptBlock}`);
    setStatus("draft");
    setPublishing(false);
    setResult(null);
    setImgB64(null);
  }, [open, image?.image_id, defaultTitle, image]);

  // Fetch WP connection status + image bytes on open
  useEffect(() => {
    if (!open || !image) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/blog-connector/wordpress/connection");
        if (!cancelled) setConnState(r.data || { connected: false });
      } catch {
        if (!cancelled) setConnState({ connected: false });
      }
    })();
    (async () => {
      setImgLoading(true);
      try {
        const r = await api.get(`/creative-studio/image/${image.image_id}`, { responseType: "blob" });
        const b = new Blob([r.data]);
        const reader = new FileReader();
        reader.onloadend = () => {
          if (!cancelled) setImgB64(reader.result);   // data URI form — the backend accepts it
        };
        reader.readAsDataURL(b);
      } catch (e) {
        if (!cancelled) toast.error("Failed to fetch the image bytes");
      } finally {
        if (!cancelled) setImgLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, image?.image_id, image]);

  const publish = async () => {
    if (!title.trim()) { toast.error("Add a title first"); return; }
    if (!content.trim()) { toast.error("Add some content first"); return; }
    if (!imgB64) { toast.error("Image is still loading — try again in a moment"); return; }
    setPublishing(true);
    try {
      const r = await api.post("/blog-connector/wordpress/publish", {
        headline: title,
        content_html: content,
        excerpt: excerpt || "",
        status,
        featured_image_b64: imgB64,
        featured_image_alt: title,
        tags: image?.tags || [],
      });
      const d = r.data || {};
      setResult({ post_url: d.post_url, post_id: d.post_id });
      toast.success(
        status === "publish"
          ? "Published to WordPress! 🎉"
          : "Draft saved to WordPress."
      );
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Publish failed"));
    } finally {
      setPublishing(false);
    }
  };

  if (!open || !image) return null;

  const notConnected = connState && !connState.connected;

  return (
    <div
      data-testid="cs-blog-publish-modal"
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl rounded-2xl border p-5 max-h-[90vh] overflow-y-auto"
        style={{ background: "#0f1117", borderColor: "#232632" }}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="min-w-0">
            <div className="text-white" style={{ fontSize: 15, fontWeight: 600 }}>
              Publish to WordPress
            </div>
            <div className="type-caption text-neutral-400 truncate mt-0.5">
              Use this AI image as the featured image of a new blog post.
            </div>
          </div>
          <button
            data-testid="cs-blog-publish-close"
            onClick={onClose}
            className="h-8 w-8 rounded-full flex items-center justify-center transition"
            style={{ background: "#232632", color: "#a9b3c9" }}
          >
            <X size={14} />
          </button>
        </div>

        {/* Connection banner */}
        {connState === null ? (
          <div className="rounded-lg border p-3 mb-4 flex items-center gap-2 type-caption"
               style={{ background: "#161923", borderColor: "#232632", color: "#a9b3c9" }}>
            <Loader2 size={13} className="animate-spin" />
            Checking WordPress connection…
          </div>
        ) : connState.connected ? (
          <div className="rounded-lg border p-3 mb-4 flex items-center gap-2 type-caption"
               data-testid="cs-blog-publish-connected"
               style={{ background: "rgba(62,207,142,0.10)", borderColor: "rgba(62,207,142,0.35)", color: "#3ecf8e" }}>
            <CheckCircle2 size={13} />
            Connected to <b>{connState.site_url}</b>
            {connState.default_author_name && (
              <span className="text-neutral-400">· author: {connState.default_author_name}</span>
            )}
          </div>
        ) : (
          <div className="rounded-lg border p-4 mb-4 text-center" data-testid="cs-blog-publish-not-connected"
               style={{ background: "#161923", borderColor: "rgba(248,113,113,0.35)" }}>
            <AlertCircle size={18} className="mx-auto mb-2" style={{ color: "#f87171" }} />
            <div className="text-white mb-1" style={{ fontSize: 14, fontWeight: 600 }}>
              WordPress isn&apos;t connected yet
            </div>
            <div className="type-caption text-neutral-400 mb-3">
              Add your WordPress site and application password in Blog Publishing to start posting.
            </div>
            <a
              href="/dashboard/blog"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-full px-4 py-1.5 type-caption"
              style={{ background: "#4a9eff", color: "#ffffff", fontWeight: 600 }}
            >
              Open Blog Publishing <ExternalLink size={11} />
            </a>
          </div>
        )}

        {/* Result (if any) */}
        {result && (
          <div className="rounded-lg border p-3 mb-4 flex items-center gap-2 type-caption"
               data-testid="cs-blog-publish-result"
               style={{ background: "rgba(74,158,255,0.10)", borderColor: "rgba(74,158,255,0.35)", color: "#c4c9d4" }}>
            <CheckCircle2 size={14} style={{ color: "#4a9eff" }} />
            <span className="flex-1">
              Post #{result.post_id} saved as <b>{status}</b>.
            </span>
            {result.post_url && (
              <a href={result.post_url} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1 hover:underline" style={{ color: "#4a9eff" }}>
                View <ExternalLink size={10} />
              </a>
            )}
          </div>
        )}

        {/* Preview + form */}
        <div className="grid gap-4" style={{ gridTemplateColumns: "180px 1fr" }}>
          <div>
            {imgLoading ? (
              <div className="aspect-square rounded-lg border grid place-items-center"
                   style={{ background: "#1a1e2a", borderColor: "#232632" }}>
                <Loader2 size={18} className="animate-spin" style={{ color: "#a78bfa" }} />
              </div>
            ) : imgB64 ? (
              <img src={imgB64} alt=""
                   data-testid="cs-blog-publish-thumb"
                   className="w-full rounded-lg border object-cover aspect-square"
                   style={{ borderColor: "#232632" }} />
            ) : (
              <div className="aspect-square rounded-lg border grid place-items-center type-caption text-neutral-500"
                   style={{ background: "#1a1e2a", borderColor: "#232632" }}>
                No preview
              </div>
            )}
          </div>
          <div className="space-y-3">
            <div>
              <label className="type-caption uppercase tracking-wider text-neutral-500 mb-1 block">
                Post title
              </label>
              <input
                data-testid="cs-blog-publish-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full rounded-lg px-3 py-2 outline-none"
                style={{ background: "#1a1e2a", border: "1px solid #232632", color: "#e6e9f0", fontSize: 13 }}
              />
            </div>
            <div>
              <label className="type-caption uppercase tracking-wider text-neutral-500 mb-1 block">
                Excerpt
              </label>
              <textarea
                data-testid="cs-blog-publish-excerpt"
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value)}
                rows={2}
                className="w-full rounded-lg px-3 py-2 outline-none resize-none"
                style={{ background: "#1a1e2a", border: "1px solid #232632", color: "#e6e9f0", fontSize: 13 }}
              />
            </div>
            <div>
              <label className="type-caption uppercase tracking-wider text-neutral-500 mb-1 block">
                Content (HTML)
              </label>
              <textarea
                data-testid="cs-blog-publish-content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={5}
                className="w-full rounded-lg px-3 py-2 outline-none resize-none font-mono"
                style={{ background: "#1a1e2a", border: "1px solid #232632", color: "#e6e9f0", fontSize: 12 }}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="type-caption uppercase tracking-wider text-neutral-500">Status</span>
            <select
              data-testid="cs-blog-publish-status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="rounded-lg px-3 py-1.5 outline-none"
              style={{ background: "#1a1e2a", border: "1px solid #232632", color: "#e6e9f0", fontSize: 12 }}>
              <option value="draft">Draft</option>
              <option value="publish">Publish now</option>
            </select>
          </div>
          <button
            data-testid="cs-blog-publish-submit"
            onClick={publish}
            disabled={publishing || notConnected || imgLoading || !imgB64}
            className="rounded-full px-5 py-2 font-medium text-white transition disabled:opacity-50 flex items-center gap-2"
            style={{ background: "linear-gradient(135deg, #4a9eff 0%, #a78bfa 100%)" }}>
            {publishing ? (<><Loader2 size={14} className="animate-spin" /> Publishing…</>) : (<><Send size={14} /> {status === "publish" ? "Publish now" : "Save as draft"}</>)}
          </button>
        </div>
      </div>
    </div>
  );
}
